package org.example.project.screens_lizenz

import androidx.compose.runtime.*
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.example.project.dashboard.ScreenManager
import org.example.project.manager.LumaticaApiManager
import org.example.project.service.LocalScreenInternalTitleVisible
import org.example.project.service.ScreenConfigApi
import org.example.project.cleaning.SharedWorkspaceStyle
import org.example.project.settings.*
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.Element
import org.w3c.dom.events.Event
import kotlin.math.ceil

private const val LIZ_ROW_GRID = "minmax(240px, 1fr) 62px 100px"
private const val LIZ_MIN_COL_W = 430
private const val LIZ_BLOCK_GAP = 32

private fun lizenzApiPart(value: String): String =
    js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String

private fun screenConfigPart(config: Map<String, Map<String, String>>): String =
    config.entries.joinToString(";") { (screenKey, settings) ->
        "$screenKey:${settings.entries.joinToString(",") { "${it.key}=${it.value}" }}"
    }

private suspend fun loadLizenzScreenConfig(): Map<String, Map<String, String>> {
    val raw = runCatching { LumaticaApiManager.call("screenlizenzverwaltung/screenconfig") }.getOrDefault("")
    if (raw.isBlank() || raw == "null") return emptyMap()
    return ScreenConfigApi.decodeScreenConfig(raw)
}

private suspend fun saveLizenzScreenConfig(config: Map<String, Map<String, String>>): Map<String, Map<String, String>> {
    val raw = runCatching {
        LumaticaApiManager.call("screenlizenzverwaltung/save-screenconfig/${lizenzApiPart(screenConfigPart(config))}")
    }.getOrDefault("")
    if (raw.isBlank() || raw == "null") return config
    return ScreenConfigApi.decodeScreenConfig(raw).ifEmpty { config }
}

private fun lizScreenTyp(key: String): String =
    if (key.contains("Mobile", ignoreCase = true)) "Mobile" else "Desktop"

@Composable
fun LIZMaskenLizenzierung() {
    Style(SharedWorkspaceStyle)

    val scope = rememberCoroutineScope()
    val internalTitleVisible = LocalScreenInternalTitleVisible.current
    val cusScreens = remember {
        ScreenManager.licensableCusScreenKeys()
            .sortedBy { ScreenManager.administrativeLabelForKey(it).lowercase() }
            .map { key -> All(key = key, label = ScreenManager.administrativeLabelForKey(key)) }
    }

    var config by remember { mutableStateOf(ScreenManager.currentScreenConfig()) }
    var search by remember { mutableStateOf("") }
    var numCols by remember { mutableStateOf(1) }
    var containerEl by remember { mutableStateOf<Element?>(null) }

    fun measure() {
        val w = containerEl?.getBoundingClientRect()?.width ?: return
        if (w <= 0) return
        numCols = ((w + LIZ_BLOCK_GAP) / (LIZ_MIN_COL_W + LIZ_BLOCK_GAP)).toInt().coerceIn(1, 8)
    }

    DisposableEffect(Unit) {
        val listener: (Event) -> Unit = { measure() }
        window.addEventListener("resize", listener)
        onDispose { window.removeEventListener("resize", listener) }
    }

    LaunchedEffect(Unit) {
        config = loadLizenzScreenConfig()
        ScreenManager.applyScreenConfig(config)
    }

    val filteredScreens = if (search.isEmpty()) cusScreens
    else cusScreens.filter {
        it.label.contains(search, ignoreCase = true) || it.key.contains(search, ignoreCase = true)
    }

    val columns = if (filteredScreens.isEmpty() || numCols == 0) listOf(emptyList())
    else filteredScreens.chunked(ceil(filteredScreens.size / numCols.toDouble()).toInt())

    Div({ classes(SharedWorkspaceStyle.workspace) }) {
        Div({ classes(SharedWorkspaceStyle.side, SharedWorkspaceStyle.content); style { width(100.percent) } }) {
            Div({
                classes(SharedWorkspaceStyle.contentInner)
                style {
                    maxWidth(100.percent)
                    overflowY("auto")
                    flex(1)
                    minHeight(0.px)
                }
            }) {

                if (internalTitleVisible) {
                    Div({ classes(SharedWorkspaceStyle.rightTitle) }) {
                        Span({ classes(SharedWorkspaceStyle.sectionTitle) }) { Text("Lizenzierungsverwaltung") }
                    }
                }

                // ── Actionbar zentriert ──────────────────────────────────
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.Center)
                        alignItems(AlignItems.Center)
                        gap(22.px)
                        flexWrap(FlexWrap.Wrap)
                    }
                }) {
                    Input(InputType.Text) {
                        classes(SharedWorkspaceStyle.textInput)
                        value(search)
                        placeholder("Screen suchen…")
                        onInput { search = it.value }
                        style { width(220.px) }
                    }
                    LumaticaButton(
                        label = "Speichern",
                        onClick = {
                            scope.launch {
                                config = saveLizenzScreenConfig(config)
                                ScreenManager.applyScreenConfig(config)
                                org.example.project.service.GlobalManager.triggerSessionConfigReload()
                            }
                        },
                        variant = LumaticaButtonVariant.Action,
                        heightPx = 42
                    )
                    LumaticaButton(
                        label = "Alle Lizenziert",
                        onClick = {
                            config = cusScreens.fold(config) { acc, entry ->
                                acc + (entry.key to ((acc[entry.key] ?: emptyMap()) + ("lizenzpflichtig" to "true")))
                            }
                        },
                        variant = LumaticaButtonVariant.Standard,
                        heightPx = 42
                    )
                    LumaticaButton(
                        label = "Alle Frei",
                        onClick = {
                            config = cusScreens.fold(config) { acc, entry ->
                                acc + (entry.key to ((acc[entry.key] ?: emptyMap()) + ("lizenzpflichtig" to "false")))
                            }
                        },
                        variant = LumaticaButtonVariant.Standard,
                        heightPx = 42
                    )
                }

                Div({
                    style {
                        alignSelf("center")
                        width(600.px)
                        maxWidth(100.percent)
                        height(1.px)
                        backgroundColor(rgba(255, 255, 255, 0.14))
                        marginTop(40.px)
                        marginBottom(40.px)
                    }
                })

                // ── Grid ────────────────────────────────────────────────
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.Center)
                        width(100.percent)
                        paddingBottom(40.px)
                    }
                    ref { el ->
                        containerEl = el
                        window.requestAnimationFrame { measure() }
                        onDispose { containerEl = null }
                    }
                }) {
                    Div({
                        style {
                            display(DisplayStyle.Grid)
                            gridTemplateColumns("repeat($numCols, ${LIZ_MIN_COL_W}px)")
                            gap(LIZ_BLOCK_GAP.px)
                            justifyContent(JustifyContent.Center)
                            boxSizing("border-box")
                        }
                    }) {
                        columns.forEachIndexed { i, columnKeys ->
                            Div({
                                style {
                                    display(DisplayStyle.Flex)
                                    flexDirection(FlexDirection.Column)
                                    minWidth(0.px)
                                    if (i < columns.lastIndex) {
                                        property("border-right", "1px solid rgba(255,255,255,0.07)")
                                        paddingRight(20.px)
                                    }
                                }
                            }) {
                                Div({
                                    classes(SharedWorkspaceStyle.permissionHeader)
                                    style { gridTemplateColumns(LIZ_ROW_GRID) }
                                }) {
                                    Span { Text("Screenname") }
                                    Span { Text("Typ") }
                                    Span { Text("Lizenzpflicht") }
                                }
                                columnKeys.forEach { entry ->
                                    val key = entry.key
                                    val lizenziert = (config[key]?.get("lizenzpflichtig") ?: "true") == "true"
                                    Div({
                                        classes(SharedWorkspaceStyle.permissionRow)
                                        style { gridTemplateColumns(LIZ_ROW_GRID) }
                                    }) {
                                        Span({
                                            classes(SharedWorkspaceStyle.permissionName)
                                            style {
                                                width(100.percent)
                                                property("white-space", "normal")
                                                property("overflow", "visible")
                                                property("text-overflow", "clip")
                                                property("overflow-wrap", "anywhere")
                                                property("line-height", "1.35")
                                            }
                                        }) { Text(entry.label) }
                                        Span({
                                            style { fontSize(13.px); color(Color.white) }
                                        }) { Text(lizScreenTyp(key)) }
                                        LumaticaCheckbox(
                                            checked = lizenziert,
                                            onChange = { isChecked ->
                                                config = config + (key to ((config[key] ?: emptyMap()) + ("lizenzpflichtig" to isChecked.toString())))
                                            }
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
